import { useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useToastContext } from '@/store/contexts/ToastContext'
import { useLanguage } from '@/hooks/useLanguage'
import { useUser } from '@/hooks/useUser'
import { SERVICE_NAME } from '@/config/api'
import { userApi } from '@/services/api/user.api'
import { t } from '@/utils/i18n'
import { getLoginErrorMessage } from '@/utils/loginErrorUtils'
import './OAuthCallback.css'

/** hash fragment 파싱 (id_token 등 값에 '=' 포함될 수 있으므로 첫 번째 '='만 구분) */
function parseHashParams(hash: string): Record<string, string> {
  const params: Record<string, string> = {}
  if (!hash || !hash.startsWith('#')) return params
  const q = hash.slice(1).split('&')
  for (const pair of q) {
    const eq = pair.indexOf('=')
    if (eq === -1) continue
    const k = decodeURIComponent(pair.slice(0, eq).replace(/\+/g, ' '))
    const v = decodeURIComponent(pair.slice(eq + 1).replace(/\+/g, ' '))
    if (k) params[k] = v
  }
  return params
}

function OAuthCallback() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { showToast } = useToastContext()
  const { language } = useLanguage()
  const { handleOAuthCallback } = useUser()
  /** 콜백 처리 1회만 실행 (useEffect 재실행 시 API/로그 무한 반복 방지) */
  const processedRef = useRef(false)

  // window.opener 접근 시도
  try {
    if (window.opener) {
      // noop
    }
  } catch (e) {
    console.warn('[OAuthCallback] window.opener 접근 실패 (COOP 정책):', e)
  }

  useEffect(() => {
    if (processedRef.current) return
    processedRef.current = true

    // React 앱이 완전히 로드될 때까지 약간의 지연 (팝업 창에서 중요)
    const processCallback = () => {
      const code = searchParams.get('code')
      const hashForOauth = parseHashParams(window.location.hash || '')
      // Apple 웹 콜백 등에서 error가 query가 아니라 hash(#error=...)에만 올 수 있음
      const oauthError = searchParams.get('error') || hashForOauth.error
      const state = searchParams.get('state')
      const currentPath = window.location.pathname

      // 어떤 OAuth 제공자인지 판단
      const isApple = currentPath.includes('/apple')

      // 팝업 창인지 확인 (opener가 있으면 팝업)
      // COOP 정책으로 인해 window.opener 접근이 차단될 수 있으므로 try-catch 사용
      let isPopup = false
      try {
        isPopup = !!window.opener && !window.opener.closed
      } catch (e) {
        // COOP 정책으로 인해 접근이 차단된 경우, opener가 있다고 가정
        console.warn('[OAuthCallback] window.opener 접근 차단 (COOP 정책), 팝업으로 간주')
        isPopup = true // 팝업으로 간주하고 진행
      }
      
      

      if (oauthError) {
      console.error('[OAuthCallback] OAuth 에러:', oauthError)

      // authorization_request_not_found 에러 처리
      let errorMessage: string
      if (oauthError === 'authorization_request_not_found' || oauthError.includes('authorization_request_not_found')) {
        errorMessage = t('messages.authRequestNotFound', language)
      } else if (oauthError === 'google_account_exists' || oauthError.includes('google_account_exists')) {
        errorMessage = t('messages.googleAccountExists', language)
      } else {
        const providerName = isApple ? 'Apple' : '구글'
        const genericFail =
          t('messages.loginFailed', language) || `${providerName} 로그인에 실패했습니다.`
        let decoded: string
        try {
          decoded = decodeURIComponent(oauthError)
        } catch {
          decoded = oauthError
        }
        const d = decoded.toLowerCase()
        if (d === 'access_denied' || d.includes('access_denied')) {
          errorMessage = t('messages.loginCancelled', language)
        } else if (
          d === 'user_cancelled_authorize' ||
          d.includes('user_cancelled') ||
          d.includes('user_canceled')
        ) {
          errorMessage = t('messages.loginCancelled', language)
        } else if (/^[a-z0-9._-]+$/i.test(decoded) && !/\s/.test(decoded)) {
          // OAuth 스타일 오류 코드만 넘어온 경우(예: invalid_request) 코드 대신 안내 문구
          errorMessage = genericFail
        } else {
          errorMessage = decoded || genericFail
        }
      }

        if (isPopup) {
        // 팝업 창인 경우 부모 창에 에러 메시지 전송
        const messageType = isApple ? 'APPLE_LOGIN_ERROR' : 'GOOGLE_LOGIN_ERROR'
        const sendError = () => {
          try {
            window.opener.postMessage(
              { type: messageType, error: errorMessage },
              window.location.origin
            )
          } catch (e) {
            console.error('[OAuthCallback] 부모 창에 메시지 전송 실패:', e)
          }
        }
        sendError()
        setTimeout(sendError, 100)
        setTimeout(sendError, 300)
        setTimeout(sendError, 500)
        
        // 팝업 닫기
        setTimeout(() => {
          try {
            window.close()
          } catch (e) {
            // 무시
          }
        }, 1000)
      } else {
        // 일반 페이지인 경우
        showToast(errorMessage, 'error')
        navigate('/login?error=' + encodeURIComponent(oauthError), { replace: true })
      }
      return
    }

    // Apple: id_token으로 로그인. 백엔드 form_post 플로우면 hash에, 구식이면 query에 옴.
    if (isApple) {
      const hashParams = parseHashParams(window.location.hash || '')
      const idToken = hashParams.id_token || searchParams.get('id_token')
      const stateFromHash = hashParams.state || state
      let guestId: string | undefined
      let name: string | undefined
      const stateStr = stateFromHash ?? state
      if (stateStr) {
        const parts = stateStr.split('|')
        for (const part of parts) {
          if (part.startsWith('guestId=')) {
            guestId = part.substring('guestId='.length)
          }
        }
      }
      if (hashParams.user) {
        try {
          const userObj = JSON.parse(decodeURIComponent(hashParams.user)) as {
            name?: { firstName?: string; lastName?: string }
          }
          const first = userObj?.name?.firstName ?? ''
          const last = userObj?.name?.lastName ?? ''
          name = [first, last].filter(Boolean).join(' ') || undefined
        } catch (_) {
          /* ignore */
        }
      }
      if (idToken) {
        ;(async () => {
          try {
            const response = await userApi.appleLoginWithToken(
              idToken,
              'dreamity',
              name,
              guestId
            )
            await handleOAuthCallback(response.token)
            navigate('/dreams', { replace: true })
          } catch (error: any) {
            console.error('[OAuthCallback] Apple 로그인 처리 실패:', error)
            const errorMessage = getLoginErrorMessage(
              error,
              t('messages.loginFailed', language),
              !!guestId
            )
            showToast(errorMessage, 'error')
            navigate('/login?error=' + encodeURIComponent('login_failed'), { replace: true })
          }
        })()
      } else {
        showToast(t('messages.appleTokenMissing', language), 'error')
        navigate('/login?error=' + encodeURIComponent('no_token'), { replace: true })
      }
      return
    }

    if (code) {
      // Google: code로 JWT 받기
      if (isPopup) {
        const messageType = 'GOOGLE_LOGIN_SUCCESS'
        const sendSuccess = () => {
          try {
            if (window.opener) {
              window.opener.postMessage(
                { type: messageType, code, state },
                window.location.origin
              )
            } else {
              console.warn('[OAuthCallback] window.opener가 없습니다')
            }
          } catch (e) {
            console.error('[OAuthCallback] 부모 창에 메시지 전송 실패:', e)
          }
        }
        sendSuccess()
        setTimeout(sendSuccess, 100)
        setTimeout(sendSuccess, 300)
        setTimeout(() => {
          try {
            window.close()
          } catch (e) {
            console.warn('[OAuthCallback] 팝업 닫기 실패:', e)
          }
        }, 1500)
      } else {
        const handleCallback = async () => {
          let service = SERVICE_NAME
          let guestId: string | undefined = undefined
          try {
            if (state) {
              const parts = state.split('|')
              for (const part of parts) {
                if (part.startsWith('service=')) {
                  service = part.substring('service='.length)
                } else if (part.startsWith('guestId=')) {
                  guestId = part.substring('guestId='.length)
                }
              }
            }
            const response = await userApi.googleLogin(code, service, guestId)
            await handleOAuthCallback(response.token)
            navigate('/dreams', { replace: true })
          } catch (error: any) {
            console.error('[OAuthCallback] 로그인 처리 실패:', error)
            const errorMessage = getLoginErrorMessage(error, t('messages.loginFailed', language), !!guestId)
            showToast(errorMessage, 'error')
            navigate('/login?error=' + encodeURIComponent('login_failed'), { replace: true })
          }
        }
        handleCallback()
      }
    } else {
      console.warn('[OAuthCallback] code 없음')
      if (isPopup) {
        const messageType = 'GOOGLE_LOGIN_ERROR'
        const sendError = () => {
          try {
            window.opener.postMessage(
              { type: messageType, error: t('messages.authCodeMissing', language) },
              window.location.origin
            )
          } catch (e) {
            console.error('[OAuthCallback] 부모 창에 메시지 전송 실패:', e)
          }
        }
        sendError()
        setTimeout(() => {
          try {
            window.close()
          } catch (e) {
            // 무시
          }
        }, 500)
      } else {
        navigate('/login?error=' + encodeURIComponent('no_code'), { replace: true })
      }
    }
    } // processCallback 함수 닫기
    
    // React 앱이 완전히 로드될 때까지 약간의 지연 (팝업 창에서 중요)
    // 팝업 창이 백엔드에서 리다이렉트되어 올 때 React 앱이 아직 로드되지 않았을 수 있음
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', processCallback)
      return () => {
        document.removeEventListener('DOMContentLoaded', processCallback)
      }
    } else {
      // 이미 로드된 경우 약간의 지연 후 처리 (React Router가 완전히 초기화될 때까지)
      setTimeout(processCallback, 100)
    }
  }, [searchParams, navigate, showToast, language, handleOAuthCallback])

  return (
    <div className="oauth-callback">
      <div className="oauth-callback__content">
        <div className="oauth-callback__spinner"></div>
        <p>{t('messages.processing', language) || '처리중입니다...'}</p>
      </div>
    </div>
  )
}

export default OAuthCallback

